import { useCallback, useEffect, useRef } from "react";
import { toastManager } from "@salestrak/ui";

import type { UserLocationError } from "#app/lib/hooks/use-user-location";

// One dismissal lasts the page load: the toast comes back on reload if the
// browser still has no fix, but not on every visit to the map in between.
// Module state, not React state, because the map screen unmounts between
// visits and the flag must outlive it.
let dismissedThisPageLoad = false;

/**
 * Explains a missing location with a persistent warning toast while the map
 * is on screen. The locate action is hidden until the browser delivers a fix,
 * so the toast is the one place that says why and carries the retry.
 *
 * Lifecycle: it appears when the location request fails (never while the
 * native prompt is still open — that window has no error yet), leaves when a
 * fix arrives or the map unmounts, and stays away for the rest of the page
 * load once the viewer dismisses it. `show()` overrides that — a person who
 * presses the C shortcut has asked to see it again.
 */
export function useLocationBlockedToast({
  error,
  onRetry,
}: {
  error: UserLocationError | null;
  onRetry: () => void;
}): { show: () => void } {
  const toastIdRef = useRef<string | null>(null);
  // `onClose` fires for the viewer's ✕ and for our own close() alike; only the
  // viewer's counts as a dismissal.
  const closingProgrammaticallyRef = useRef(false);
  const errorRef = useRef(error);
  errorRef.current = error;
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  const closeToast = useCallback(() => {
    if (!toastIdRef.current) return;
    closingProgrammaticallyRef.current = true;
    toastManager.close(toastIdRef.current);
    closingProgrammaticallyRef.current = false;
    toastIdRef.current = null;
  }, []);

  const openToast = useCallback(() => {
    const current = errorRef.current;
    if (!current || toastIdRef.current) return;
    toastIdRef.current = toastManager.add({
      title: current.title,
      description: current.description,
      type: "warning",
      // Persistent — the library renders its dismiss ✕ for timeout 0.
      timeout: 0,
      actionProps: {
        children: "Try again",
        // Toast.Action does not close its toast, and it should not here: on a
        // second failure the explanation is still the right thing to show. A
        // success closes it through the error → null effect below.
        onClick: () => onRetryRef.current(),
      },
      onClose: () => {
        if (!closingProgrammaticallyRef.current) dismissedThisPageLoad = true;
        toastIdRef.current = null;
      },
    });
  }, []);

  useEffect(() => {
    if (!error) {
      closeToast();
      return;
    }
    if (toastIdRef.current) {
      // The kind can change between attempts (denied → unavailable once the
      // site is allowed but the OS toggle is off); keep the copy current.
      toastManager.update(toastIdRef.current, {
        title: error.title,
        description: error.description,
      });
      return;
    }
    if (!dismissedThisPageLoad) openToast();
  }, [error, closeToast, openToast]);

  // Leaving the map takes the toast with it; the next visit re-raises it
  // unless it was dismissed.
  useEffect(() => closeToast, [closeToast]);

  const show = useCallback(() => {
    dismissedThisPageLoad = false;
    openToast();
  }, [openToast]);

  return { show };
}
